import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchHit } from "../../../../creditSearch";
import horizontalSplit from "./utils/horizontalSplit";
import splitArray from "./utils/splitArray";
import { clampColumnWidthPct } from "../../../../utils/maxColumnWidth";
import "../../../../../../Components/Radio/Radio.css";
import { splitGutter } from "./gutterUtils";

// import {BsFillCircleFill, BsDiamondFill, BsFillSquareFill,} from "react-icons/bs";
// import { TbMinusVertical } from "react-icons/tb";
// import { ImCross } from "react-icons/im";
// const getParaIcon = (shape) => {
//   switch (shape) {
//     case "circle":
//       return <BsFillCircleFill />;
//     case "square":
//       return <BsFillSquareFill />;
//     case "diamond":
//       return <BsDiamondFill />;
//     case "lineVertical":
//       return <TbMinusVertical />;
//     case "cross":
//       return <ImCross />;
//     default:
//       return <BsFillCircleFill />;
//   }
// };

const getAlignment = (text_alignment, columns) => {
  const cols = Number(columns) || 1;
  switch (text_alignment) {
    case "left":
      if (cols === 1) {
        return [" text-start "];
      } else if (cols === 2) {
        return [" text-start ", " text-start "];
      } else if (cols === 3)
        return [" text-start ", " text-start ", " text-start "];
      else if (cols === 4) {
        return [" text-start ", " text-start ", " text-start ", " text-start "];
      } else {
        return [" text-start ", " text-start ", " text-start ", " text-start ", " text-start ",];
      }

    case "justify":
      if (cols === 1) {
        return [" text-start "];
      } else if (cols === 2) {
        return [" text-start ", " text-end "];
      } else if (cols === 3)
        return [" text-start ", " text-center ", " text-end "];
      else if (cols === 4) {
        return [" text-start ", " text-start ", " text-end ", " text-end "];
      } else {
        return [" text-start ", " text-start ", " text-center ", " text-end ", " text-end ",];
      }

    case "right":
      if (cols === 1) {
        return [" text-end "];
      } else if (cols === 2) {
        return [" text-end ", " text-end "];
      } else if (cols === 3)
        return [" text-end ", " text-end ", " text-end "];
      else if (cols === 4) {
        return [" text-end ", " text-end ", " text-end ", " text-end "];
      } else {
        return [" text-end ", " text-end ", " text-end ", " text-end ", " text-end ",];
      }

    case "center":
      if (cols === 1) {
        return [" text-center "];
      } else if (cols === 2) {
        return [" text-center ", " text-center "];
      } else if (cols === 3)
        return [" text-center ", " text-center ", " text-center "];
      else if (cols === 4) {
        return [" text-center ", " text-center ", " text-center ", " text-center ",];
      } else {
        return [" text-center ", " text-center ", " text-center ", " text-center ", " text-center ",];
      }
    default:
      // Prefer center when alignment is missing/unknown — text-end made
      // single-column nameonly look right-pinned in export crops.
      if (cols === 1) {
        return [" text-center "];
      } else if (cols === 2) {
        return [" text-end ", " text-start "];
      } else if (cols === 3)
        return [" text-end ", " text-center ", " text-start "];
      else if (cols === 4) {
        return [" text-end ", " text-end ", " text-start ", " text-start "];
      } else {
        return [" text-end ", " text-end ", " text-center ", " text-start ", " text-start ",];
      }
  }
};

const getPadding = (textAlignment, noOfCols, colIdx, gutter) => {
  const cellGutter = splitGutter(gutter);
  let pl = 0;
  let pr = 0;
  if (textAlignment === "gutter") {
    if (noOfCols == 2) {
      if (colIdx == 0) pr = cellGutter;
      if (colIdx == 1) pl = cellGutter;
    }
    if (noOfCols == 3 || noOfCols == 5) {
      if (colIdx > 0) pl = cellGutter;
      if (colIdx < noOfCols - 1) pr = cellGutter;
    }
    if (noOfCols == 4) {
      if (colIdx == 1) pr = cellGutter;
      if (colIdx == 2) pl = cellGutter;
    }
  }
  return {
    paddingLeft: `${pl}px`,
    paddingRight: `${pr}px`,
  };
};

function NameOnly2({items, options, fontStyle, settings, idx,}) {
  const bulletRefs = useRef([]);

  const filteredItems = useMemo(() => {
 // Drop empty rows entirely. The name <p> and its bullet <span> live in the
  // SAME mapped <div>, so removing the item here removes BOTH — an empty item
  // never leaves a lone bullet. Whitespace-only names count as empty too.
  return (items || []).filter((it) => it?.name && String(it.name).trim() !== "");
}, [items]);

// Keep the refs array the same length as the current items. Without this, a
// block that shrinks (fewer names) leaves STALE, detached <div> refs behind.
// recompute() reads offsetTop on those dead nodes (which returns 0), groups
// them into the first row, and then hides a dead node instead of the real
// line-end bullet — so a bullet stays visible at the end of a line. This is
// worst in the render, where the block is rebuilt at videoWidth.
bulletRefs.current.length = filteredItems.length;

const sortedItems = useMemo(() => {
  const copy = [...filteredItems];

  if (options?.column_order === "ascending") {
    copy.sort((a, b) => a.name.localeCompare(b.name));
  } else if (options?.column_order === "descending") {
    copy.sort((a, b) => b.name.localeCompare(a.name));
  }

  return copy;
}, [filteredItems, options?.column_order]);

  const columnCount = Math.max(1, Number(options?.columns) || 1);
  const columnWidth = clampColumnWidthPct(options?.column_width, columnCount);
  const totalWidthPct = Math.min(columnWidth * columnCount, 100);
  const columnFlow = options?.column_flow;
  const balanceColumns = options?.balance_columns;

  const arraySplitData = useMemo(() => {
    const splitOptions = {
      columns: columnCount,
      balance_columns: balanceColumns,
    };
    const raw =
      columnFlow === "vertical"
        ? splitArray(sortedItems, splitOptions)?.ans
        : horizontalSplit(sortedItems, splitOptions)?.ans;
    return Array.from({ length: columnCount }, (_, i) => raw?.[i] || []);
  }, [sortedItems, columnCount, columnFlow, balanceColumns]);

  const textAlignment = getAlignment(options?.text_alignment, columnCount);

  const bulletAlignJSON = { left: "start", right: "end", center: "center" };
  // Paragraph mode only maps left/right/center — fall back so gutter/justify
  // left over from columns mode don't produce justify-undefined.
  const bulletJustify =
    bulletAlignJSON[options.text_alignment] || bulletAlignJSON.center;

  const [bulletClass, setBulletClass] = useState("");

  useEffect(() => {
    if (!options?.bullets) return;

    // Hide the last bullet on each visual line so the line ends on a name.
    // Compare each pair to the next pair's offsetTop — more stable than
    // grouping by exact Y after font load / zoom / refresh (those Ys can
    // differ by a subpixel and leave a real end-of-row bullet visible).
    const recompute = () => {
      const list = document.getElementById("list" + idx);
      if (!list) return;
      const pairs = [...list.children];
      pairs.forEach((el, i) => {
        const bullet = el.querySelector(".bullet");
        if (!bullet) return;
        const next = pairs[i + 1];
        const lineSlop = Math.max(4, (el.offsetHeight || 0) * 0.45);
        const lastOnLine = !next || next.offsetTop > el.offsetTop + lineSlop;
        bullet.classList.toggle("rmNewClassbullets", lastOnLine);
      });
    };

    recompute();
    const raf = requestAnimationFrame(() => {
      recompute();
      requestAnimationFrame(recompute);
    });
    document.fonts?.ready?.then?.(recompute);

    const list = document.getElementById("list" + idx);
    let ro;
    if (list && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(recompute);
      ro.observe(list);
      if (list.parentElement) ro.observe(list.parentElement);
    }
    window.addEventListener("resize", recompute);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [filteredItems, options, fontStyle?.nameOnlyStyle, idx, bulletClass]);

  useEffect(() => {
    const bulletClasses = {
      circle: "circle",
      square: "li-square",
      diamond: "diamond",
      lineVertical: "lineVertical",
      cross: "cross",
    };

    if (options.bullets !== null && options.bullets && bulletClasses[options.bullets]) {
      setBulletClass(bulletClasses[options.bullets]);
    }
  }, [options.bullets]);

  return options?.bullets ? (
    <div className="flex flex-col justify-center w-full overlaytext">
      <div id={"list" + idx} className={"FontSIzeScale  m-auto flex flex-wrap justify-" + bulletJustify}
        style={{
          ...fontStyle?.nameOnlyStyle,
          width: columnWidth + "%",
          lineHeight: fontStyle?.nameOnlyStyle?.lineHeight + 0.15,
          fontKerning: "none",
          fontFeatureSettings: '"kern" 0',
          textRendering: "geometricPrecision",
          WebkitFontSmoothing: "antialiased",
          // Each line ends with an invisible 1.2em bullet (kept for stable
          // wrapping). Shift with relative `left` (not transform) so html2canvas
          // card capture keeps names optically centered — transform translateX
          // was landing ~9% right of center in the card PNG.
          position: "relative",
          left:
            bulletJustify === "center"
              ? "0.6em"
              : bulletJustify === "end"
              ? "1.2em"
              : undefined,
        }}
      >
        {filteredItems.map((item, i) => (
          <div
            key={i}
            ref={(el) => (bulletRefs.current[i] = el)}
            className="name-only-bullet-pair grid grid-flow-col items-stretch justify-center"
          >
            <p className="FontSIzeScale m-0 p-0"
              style={{
                ...fontStyle?.nameOnlyStyle,
                fontKerning: "none",
                fontFeatureSettings: '"kern" 0',
                textRendering: "geometricPrecision",
                WebkitFontSmoothing: "antialiased",
              }}
            >
              <SearchHit text={item.name} />
            </p>

            <span className={`FontSIzeScale bullet ${bulletClass}`}>
              <span
                aria-hidden="true"
                className={`bullet-glyph ${bulletClass}`}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div
      className="overlaytext"
      style={{
        width: `${totalWidthPct}%`,
        margin: "0 auto",
        display: "grid",
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        wordBreak: "break-word",
      }}
    >
      {arraySplitData.map((colItems, index) => (
        <div
          key={index}
          className={`flex flex-col break-words ${textAlignment[index] || ""}`}
          style={{
            minWidth: 0,
            ...getPadding(
              options.text_alignment,
              columnCount,
              index,
              settings.gutter,
            ),
          }}
        >
          {colItems?.map((item, itemIdx) => (
            <p
              key={item?._id || `${item?.name || "name"}-${itemIdx}`}
              className={` FontSIzeScale ${textAlignment[index] || ""}`}
              style={{
                ...fontStyle?.nameOnlyStyle,
                lineHeight: fontStyle?.nameOnlyStyle.lineHeight,
                fontKerning: "none",
                fontFeatureSettings: '"kern" 0',
                textRendering: "geometricPrecision",
                WebkitFontSmoothing: "antialiased",
              }}
            >
              <SearchHit text={item.name} />
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

const areNameOnlyPropsEqual = (previous, next) =>
  previous.items === next.items &&
  previous.options === next.options &&
  previous.settings === next.settings &&
  previous.idx === next.idx &&
  previous.fontStyle?.nameOnlyStyle === next.fontStyle?.nameOnlyStyle;

export default React.memo(NameOnly2, areNameOnlyPropsEqual);
